// React & Hooks
import { memo, useEffect, useState } from "react";

// Project Specific Types & Constants
import { TireEditProps } from "./types";
import { CALCULATION_TIRE_EDIT_FORM_SCHEMA } from "./validation";
import { initialTireEditForm } from "services/api/calculations/tire/consts";
import { initialTireManufacturerDataWithPagination } from "./consts";

// API & Services
import {
  createTireAPI,
  getTireAPI,
  getTireManufacturersAPI,
  updateTireAPI,
} from "services/api/calculations/tire/api";
import { DataWithPagination } from "services/api";
import {
  TireEditFormValues,
  TireManufacturer,
  TireManufacturerRow,
} from "services/api/calculations/tire/types";
import { transformTireManufacturerPrice } from "services/api/calculations/tire/transformations";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";

// Forms & Validation
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { useHandleTextFieldChange } from "services/forms/hooks";

// Helpers
import { useTranslate } from "helpers";

const TireEditCard: React.FC<TireEditProps> = (props) => {
  const { t } = useTranslate();
  const { tireId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [tireManufacturers, setTireManufacturers] = useState<
    DataWithPagination<TireManufacturerRow>
  >(initialTireManufacturerDataWithPagination);

  const [formValues, setFormValues] =
    useState<TireEditFormValues>(initialTireEditForm);

  useEffect(() => {
    if (tireId) {
      fetchTire(tireId);
    }
    fetchTireManufacturers();
  }, [tireId]);

  const fetchTire = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTireEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTireAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }
    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchTireManufacturers = async () => {
    setIsLoading(true);

    let result;
    try {
      result = await getTireManufacturersAPI(tireManufacturers.pagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setTireManufacturers(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_TIRE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!tireId) {
        result = await updateTireAPI(tireId, formValues);
      } else {
        result = await createTireAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleManufacturerPriceChange = (
    manufacturerId: string | null | undefined,
    value: number
  ) => {
    if (!manufacturerId) return;

    setFormValues((prevValues) => {
      const manufacturers = prevValues.manufacturers as TireManufacturer[];

      // Provjera da su podaci konzistentni
      const existingIndex = manufacturers.findIndex(
        (m) =>
          String(m.tireManufacturer?.id) ===
          String(manufacturerId)
      );

      let updatedManufacturers: TireManufacturer[];

      if (existingIndex !== -1) {
        // Ako već postoji, ažuriraj postojeći element
        updatedManufacturers = manufacturers.map((m) =>
          String(m.tireManufacturer?.id) ===
          String(manufacturerId)
            ? { ...m, price: value }
            : m
        );
      } else {
        // Ako ne postoji, dodaj novi element u niz
        updatedManufacturers = [
          ...manufacturers,
          {
            price: value,
            tireManufacturer: { id: manufacturerId, name: "" },
          },
        ];
      }

      return {
        ...prevValues,
        manufacturers: updatedManufacturers,
      };
    });
  };

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 ">
                  <div className="flex flex-row w-full mb-3">
                    <div className="flex-1">
                      <TextField
                        type="number"
                        name="width"
                        label={t("admin.calculation.setting.tire.add.width")}
                        placeholder={t(
                          "admin.calculation.setting.tire.width.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.width}
                        value={formValues.width}
                        required
                        endIcon=""
                      ></TextField>
                    </div>
                    <div className="flex-1">
                      <TextField
                        type="number"
                        name="height"
                        label={t("admin.calculation.setting.tire.add.height")}
                        placeholder={t(
                          "admin.calculation.setting.tire.height.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.height}
                        value={formValues.height}
                        required
                        endIcon=""
                      ></TextField>
                    </div>
                    <div className="flex-1">
                      <TextField
                        type="number"
                        name="radius"
                        label={t("admin.calculation.setting.tire.add.radius")}
                        placeholder={t(
                          "admin.calculation.setting.tire.radius.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.radius}
                        value={formValues.radius}
                        required
                        endIcon=""
                      ></TextField>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mb-5 ">
                  <div className="flex-1 ">
                    <TextField
                      type="number"
                      name="changePrice"
                      label={t(
                        "admin.calculation.setting.tire.add.changeprice"
                      )}
                      placeholder={t(
                        "admin.calculation.setting.tire.changeprice.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.changePrice}
                      value={formValues.changePrice}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="number"
                      name="hotelPrice"
                      label={t("admin.calculation.setting.tire.add.hotelprice")}
                      placeholder={t(
                        "admin.calculation.setting.tire.hotelprice.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.hotelPrice}
                      value={formValues.hotelPrice}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                  <div className="flex-1 "></div>
                </div>
                <div className="bg-slate-50 border border-slate-300 rounded-sm p-4">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex-1 mb-2">
                      {t("admin.calculation.setting.tire.brand")}
                    </div>
                    <div className="flex-1 mb-2">
                      {t("admin.calculation.setting.tire.price")}
                    </div>
                  </div>
                  {tireManufacturers.data.map((item) => {
                    const tireManufacturerId = item.id || "";
                    return (
                      <div
                        key={tireManufacturerId}
                        className="flex items-center gap-4"
                      >
                        <div className="flex-1 mb-2 text-sm">{item.name}</div>
                        <div className="flex-1 mb-2">
                          <div className="flex mb-4">
                            <div className="flex flex-col">
                              <label className="text-xs uppercase pb-2 text-slate-700">
                                Ljetne gume
                              </label>
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.budget.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.standard.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.premium.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-xs uppercase pb-2 text-slate-700">
                                Zimske gume
                              </label>
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.budget.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.standard.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.premium.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-xs uppercase pb-2 text-slate-700">
                                Cjelogodišnje
                              </label>
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.budget.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.standard.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                              <TextField
                                type="number"
                                id="tireManufacturerId"
                                name={`${tireManufacturerId}`}
                                placeholder={t(
                                  "admin.calculation.setting.tire.price.premium.placeholder"
                                )}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  handleManufacturerPriceChange(
                                    tireManufacturerId,
                                    Number(e.target.value)
                                  )
                                }
                                value={transformTireManufacturerPrice(
                                  formValues.manufacturers as TireManufacturer[],
                                  tireManufacturerId
                                )}
                                required
                                error={errors?.[tireManufacturerId]}
                                groupAppend="€"
                                cssClassName="bg-white"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TireEditCard);
